import { isValidElement, useLayoutEffect, useRef, useState } from 'react'

import {
  Icon,
  IconAlertCircle,
  IconArrowRight,
  IconChevronRight,
} from '@tabler/icons-react'
import { Command } from 'cmdk'
import { useRouter } from 'next/router'

import { CommandAction, CommandGroup } from './config'

import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useLocale } from '@/context/localeContext'
import { useProject } from '@/context/projectContext'

import { buildPassTheme } from 'src/styles/theme'

const renderIcon = (item: CommandAction) => {
  if (item.icon) {
    if ((item.icon as Icon).displayName?.startsWith('Icon')) {
      const Icon = item.icon as Icon
      return <Icon size={16} />
    }
    if (isValidElement(item.icon)) {
      return item.icon
    }
  }
  if (item.href) return <IconArrowRight size={16} />
  return null
}

const renderTitle = (item: CommandAction, name: string) => {
  if (item.href) {
    return (
      <>
        Go to <strong>{name}</strong>
      </>
    )
  }

  return name
}

type RenderItemProps = {
  item: CommandAction | CommandGroup
  addPage: (page: string) => void
  searchText: string
  closeModal: () => void
}

export const RenderItem: React.FC<RenderItemProps> = ({
  item,
  addPage,
  searchText,
  closeModal,
}) => {
  const router = useRouter()
  const { t } = useLocale()
  const commandItemRef = useRef<HTMLDivElement>(null)

  const { isProjectSelected } = useProject()

  const [content, setContent] = useState<string>(null)

  useLayoutEffect(() => {
    if (commandItemRef.current) {
      setContent(commandItemRef.current.textContent?.trim().toLowerCase())
    }
  }, [])

  if ('groupActions' in item) {
    return (
      <Command.Group heading={item.groupTitle}>
        {item.groupActions.map((action) => (
          <RenderItem
            key={action.id}
            item={action}
            addPage={addPage}
            searchText={searchText}
            closeModal={closeModal}
          />
        ))}
      </Command.Group>
    )
  }

  if (item.requiresProject && !isProjectSelected)
    return (
      <Command.Item>
        <Flexbox alignItems="center" gap="1rem" style={{ width: '100%' }}>
          <IconAlertCircle
            size={16}
            color={buildPassTheme.colour.semantic.warningDark}
          />
          <Text variant="label02" semantic="warningDark">
            Select a project to <strong>{t(item.name)}</strong>
          </Text>
        </Flexbox>
      </Command.Item>
    )

  const onItemSelect = () => {
    if (item.subActions?.length) return addPage(item.id)
    if (item.perform) {
      return item.perform(closeModal)
    }
    if (item.href) return router.push(item.href) // wrapping in <Link /> made the click area weird
  }

  return (
    <Command.Item
      ref={commandItemRef}
      onSelect={onItemSelect}
      value={item.id + ' ' + content} // this is what is searched on
    >
      <Flexbox
        alignItems="center"
        gap="1rem"
        justifyContent="space-between"
        style={{ width: '100%', color: buildPassTheme.colour.secondary.base }}
      >
        <Flexbox alignItems="center" gap="1rem" style={{ width: '100%' }}>
          <Flexbox style={{ flexShrink: 0 }} alignItems="center">
            {renderIcon(item)}
          </Flexbox>
          <Flexbox
            flexDirection="column"
            gap="0.25rem"
            style={{ flexGrow: '1' }}
          >
            <Text variant="label02">{renderTitle(item, t(item.name))}</Text>
            {item.description && (
              <Text variant="caption01">{item.description}</Text>
            )}
          </Flexbox>
          {item.extra}
        </Flexbox>
        {!!item.subActions?.length && <IconChevronRight size={16} />}
      </Flexbox>
    </Command.Item>
  )
}
